import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useToaster } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import {
  getJobJourneys,
  JobJourneysResponse,
  setJobJourneyEnabled,
  SetJobJourneyEnabledInput,
} from './api';
import { jobJourneysQueryKey, jobJourneysQueryKeyPrefix } from './query-keys';

import { availableJourneysQueryKeyPrefix, jobTriggersQueryKey } from '../job-triggers/query-keys';

/**
 * Fetch the list of globally-enabled journeys for a job, with per-job
 * resolved enabled state. Null-inherit (no per-job row) is returned as
 * isEnabled=true by the backend.
 */
export const useJobJourneys = (jobId: number, search: string) => {
  return useQuery({
    queryKey: jobJourneysQueryKey(jobId, search),
    queryFn: () => getJobJourneys(jobId, search),
    // Keep the previous search's results on screen while a new search
    // debounces in, rather than dropping to isLoading — G34 keeps the search
    // box (and the rest of the list) mounted through that gap.
    keepPreviousData: true,
  });
};

/**
 * Set the per-job availability of a journey.
 * isEnabled=null reverts to inherit (shows the journey as enabled again).
 */
export type SetJobJourneyEnabledVariables = Omit<SetJobJourneyEnabledInput, 'jobId'> & {
  /** Named in the confirmation toast; not part of the request body. */
  journeyTitle: string;
};

export const useSetJobJourneyEnabled = (jobId: number) => {
  const { t } = useScopedTranslate(locale);
  const { showError, showSuccess } = useToaster();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ journeyTitle: _title, ...input }: SetJobJourneyEnabledVariables) =>
      setJobJourneyEnabled({ ...input, jobId }),
    // The switch is controlled from journey.isEnabled, so without this it does
    // not move until a PUT plus a GET complete — and that GET makes an upstream
    // Journeys call. The company-level twin has been optimistic all along.
    //
    // The query key now carries `search`, and a mutation has no view of which
    // search the user is currently looking at — update every cached search
    // variant under the prefix rather than a single exact key.
    onMutate: async (input) => {
      await queryClient.cancelQueries(jobJourneysQueryKeyPrefix(jobId));
      const previous = queryClient.getQueriesData<JobJourneysResponse>(
        jobJourneysQueryKeyPrefix(jobId)
      );
      queryClient.setQueriesData<JobJourneysResponse>(jobJourneysQueryKeyPrefix(jobId), (current) =>
        current
          ? {
              ...current,
              results: current.results.map((journey) =>
                journey.journeyId === input.journeyId
                  ? { ...journey, isEnabled: input.isEnabled !== false }
                  : journey
              ),
            }
          : current
      );
      return { previous };
    },
    onSuccess: (_data, variables) => {
      // Under the Available/Hidden filter the toggled row leaves the list it
      // was in, so without this the row just vanishes with nothing to explain
      // it. Reverting to inherit (null) reads as available, matching both the
      // switch and the optimistic update above.
      showSuccess(variables.isEnabled === false ? t('hiddenSuccess') : t('availableSuccess'), {
        subtitle: variables.journeyTitle,
      });
    },
    onError: (_error, _input, context) => {
      context?.previous.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      showError(t('enableError'));
    },
    onSettled: () => {
      queryClient.invalidateQueries(jobJourneysQueryKeyPrefix(jobId));
      queryClient.invalidateQueries(availableJourneysQueryKeyPrefix(jobId));
      queryClient.invalidateQueries(jobTriggersQueryKey(jobId));
    },
  });
};

const locale = {
  en: {
    enableError: 'Failed to update journey availability',
    availableSuccess: 'Journey available in this job',
    hiddenSuccess: 'Journey hidden from this job',
  },
  is: {
    enableError: 'Ekki tókst að breyta því hvort Journey-ferlið sé í boði',
    availableSuccess: 'Journey-ferlið er nú í boði fyrir þetta starf',
    hiddenSuccess: 'Journey-ferlið er nú falið fyrir þetta starf',
  },
};
